import type { Role } from "@/contexts/auth-context";

// Route-level role gates — the single source of truth shared by the JSX <RoleGuard>
// wrappers in app-routes.tsx and the post-login return-to validation in
// SignInScreen.tsx (Franklin#317). Driving both off one table stops them drifting:
// add a rule here and both the guard AND the return-to check honour it.
//
// A THIRD file has to agree with this table: nav-config.ts decides what each role is
// offered. `__tests__/route-access.nav-fitness.test.tsx` is the only test that reads
// all three together — every per-file test here, in nav-config.test.ts and in the
// app-routes.* tests is green on a rule added to one file and forgotten in the others.

/** Roles permitted in the admin console group (/admin/*). Typed to `Role` so a
 * typo (e.g. "org-admin") is a compile error, and readonly so it can't be mutated. */
export const ADMIN_ROLES: ReadonlyArray<Role> = ["org_admin", "super_admin"];

/** Platform-only surfaces — super_admin exclusively (org_admin DENIED). Story 16.4's
 * legacy-cert verification queue is the first: only a super_admin may verify legacy
 * AMPS certificates (mark_legacy_cert_verified / receive_legacy_cert_for_review). */
export const SUPER_ADMIN_ROLES: ReadonlyArray<Role> = ["super_admin"];

/** Org-scoped self-service surfaces (/org-admin/*) — org_admin exclusively. Mirrors the
 * backend MATRIX posture (Story 14.1: view_org_admin_dashboard has super_admin DENIED —
 * the org comes from the actor's own claim, which a super_admin does not carry; the
 * platform-wide seat view is the 15.x org directory). */
export const ORG_ADMIN_ONLY_ROLES: ReadonlyArray<Role> = ["org_admin"];

/** Roles permitted in the clinician-only areas — the workspace group (/workspace,
 * /clients, /evaluations, /results) and the Resources tab (/resources). Admins have
 * no nav entry for these screens and are gated out at both the route guard and the
 * post-login return-to check. (FR80 was refined 2026-06-03 to clinician-only for
 * Resources, incl. legacy clinicians in onboarding, who hold role "clinician".) */
export const CLINICIAN_ROLES: ReadonlyArray<Role> = ["clinician"];

interface RouteAccessRule {
  /** Path prefix the rule governs — matches the prefix itself or any child segment. */
  prefix: string;
  /** Roles allowed past the gate; any other role is blocked. */
  allow: ReadonlyArray<Role>;
}

// Mirrors the <RoleGuard> groups in app-routes.tsx. A path matching no rule is
// reachable by any authenticated role (those routes carry no RoleGuard today).
// `as const satisfies` keeps the table immutable — no code can silently .push()
// a rule and widen the access policy without a type error.
const ROUTE_ACCESS_RULES = [
  { prefix: "/admin", allow: ADMIN_ROLES },
  // Story 14.1 — the org_admin dashboard group is org_admin ONLY (super_admin
  // is DENIED in the backend MATRIX; a nav/guard mismatch would 403).
  { prefix: "/org-admin", allow: ORG_ADMIN_ONLY_ROLES },
  // Story 16.4 — the legacy-cert queue is super_admin ONLY. A more specific rule than
  // the /admin prefix above: canRoleAccessPath uses .every(), so an org_admin passes
  // the /admin rule but fails this one → blocked (the most restrictive rule wins).
  { prefix: "/admin/legacy-certs", allow: SUPER_ADMIN_ROLES },
  // Story 11.7 — the audit console is super_admin ONLY, and this narrower rule is
  // load-bearing rather than decoration: without it an org_admin passes the /admin
  // rule above and reaches a route the API refuses with ADMIN-005. The refusal is
  // on the SURFACE, not the scope — there is no org-level RLS on the audit store,
  // so an org_admin cannot be safely scoped to their own tenant here.
  { prefix: "/admin/audit", allow: SUPER_ADMIN_ROLES },
  // Story 15.1 — platform organization provisioning is super_admin ONLY, and this
  // narrower rule is load-bearing for the same reason as the audit one above: without
  // it, /admin/orgs matches only the /admin rule, whose allow-list is ADMIN_ROLES, and
  // canRoleAccessPath resolves with .every() — so an org_admin passes and walks into
  // the platform organization directory. The backend refuses the surface outright
  // (Capability.manage_organizations is ANY_ORG, super_admin only).
  { prefix: "/admin/orgs", allow: SUPER_ADMIN_ROLES },
  // Story 15.7 — the operational console is super_admin ONLY, on its OWN path
  // segment. The segment is the point: `canRoleAccessPath` resolves with
  // .every() and this table has no exact-match rule form, so a rule written as
  // `{ prefix: "/admin", allow: SUPER_ADMIN_ROLES }` would govern /admin/users,
  // /admin/users/new and /admin/users/:userId as well and lock an org_admin out
  // of the admin user directory they are entitled to. That is a live
  // regression, not a new gate — which is why the console never mounts at the
  // bare /admin.
  { prefix: "/admin/console", allow: SUPER_ADMIN_ROLES },
  // Clinician workspace group. `/clients` also governs its child `/clients/new`
  // (the prefix matches a child segment), so no separate rule is needed for it.
  { prefix: "/workspace", allow: CLINICIAN_ROLES },
  { prefix: "/clients", allow: CLINICIAN_ROLES },
  { prefix: "/evaluations", allow: CLINICIAN_ROLES },
  { prefix: "/results", allow: CLINICIAN_ROLES },
  // Clinician-only Resources tab (Story 17.2).
  { prefix: "/resources", allow: CLINICIAN_ROLES },
  // Story 8.1 — the calibration flow (gate destination + E8 screens).
  { prefix: "/calibration", allow: CLINICIAN_ROLES },
] as const satisfies readonly RouteAccessRule[];

/**
 * Bare pathname (drop query/hash) for the route-role check. Pass a value that is
 * already safeReturnTo-sanitised (internal, single-slash), so a plain split on the
 * first `?`/`#` suffices: "/admin/users?tab=active" → "/admin/users".
 */
export function pathnameOf(value: string): string {
  return value.split(/[?#]/, 1)[0];
}

/**
 * True when `role` may reach `pathname` under the route-level role gates. Used to
 * validate a guard-captured return-to path against the *freshly* logged-in user's
 * role before replaying it, so a path stashed for a different identity (e.g. an
 * admin who signed out on /admin/users) can't bounce a clinician onto /403.
 *
 * Pass a bare pathname (no query/hash). A path matching no rule is allowed; a path
 * under a gated prefix is allowed only when `role` is in that rule's allow-list. The
 * prefix matches itself or a child segment (`/admin`, `/admin/users`) but not a
 * lexical sibling (`/administration`).
 */
export function canRoleAccessPath(pathname: string, role: Role | string | undefined): boolean {
  return ROUTE_ACCESS_RULES.every((rule) => {
    const governed = pathname === rule.prefix || pathname.startsWith(`${rule.prefix}/`);
    // `role` is an untrusted token claim (arbitrary string); widen the typed
    // allow-list to compare it without asserting it is a known Role.
    return !governed || (role !== undefined && (rule.allow as readonly string[]).includes(role));
  });
}
